import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Link, useNavigate } from "react-router-dom";
import { useSignup } from "@/hooks/useMe";
import { signupInputSchema, type SignupInput } from "@/schemas";
import { currentBrand } from "@/lib/brand";

export default function Signup() {
  const navigate = useNavigate();
  const signup = useSignup();
  const brand = currentBrand();

  const form = useForm<SignupInput>({
    resolver: zodResolver(signupInputSchema),
    defaultValues: {
      first_name: "",
      last_name: "",
      email: "",
      password: "",
      // Captures which lead-magnet site they came in via for attribution.
      source_site_slug: brand.sourceSlug,
    },
  });

  const onSubmit = form.handleSubmit(async (values) => {
    try {
      await signup.mutateAsync(values);
      navigate("/dashboard/app", { replace: true });
    } catch (err) {
      form.setError("email", {
        message: (err as Error)?.message ?? "Signup failed.",
      });
    }
  });

  return (
    <section className="container max-w-md py-16">
      <h1 className="text-2xl font-semibold">Create your {brand.name} account</h1>
      <p className="mt-1 text-sm text-muted-foreground">
        Takes 30 seconds. Then connect FreeAgent.
      </p>
      <form onSubmit={onSubmit} className="mt-6 space-y-4">
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="mb-1 block text-sm font-medium">First name</label>
            <input
              className="w-full rounded-md border bg-background px-3 py-2"
              autoFocus
              {...form.register("first_name")}
            />
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium">Last name</label>
            <input
              className="w-full rounded-md border bg-background px-3 py-2"
              {...form.register("last_name")}
            />
          </div>
        </div>
        <div>
          <label className="mb-1 block text-sm font-medium">Email</label>
          <input
            type="email"
            className="w-full rounded-md border bg-background px-3 py-2"
            {...form.register("email")}
          />
          {form.formState.errors.email && (
            <p className="mt-1 text-xs text-destructive">
              {form.formState.errors.email.message}
            </p>
          )}
        </div>
        <div>
          <label className="mb-1 block text-sm font-medium">Password</label>
          <input
            type="password"
            className="w-full rounded-md border bg-background px-3 py-2"
            {...form.register("password")}
          />
          {form.formState.errors.password && (
            <p className="mt-1 text-xs text-destructive">
              {form.formState.errors.password.message}
            </p>
          )}
        </div>
        <button
          type="submit"
          disabled={signup.isPending}
          className="w-full rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground disabled:opacity-50"
        >
          {signup.isPending ? "Creating…" : "Create account"}
        </button>
      </form>
      <p className="mt-4 text-sm text-muted-foreground">
        Already have an account?{" "}
        <Link to="/dashboard/login" className="text-foreground underline">
          Log in
        </Link>
      </p>
    </section>
  );
}
